import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import BuildIcon from "@mui/icons-material/Build";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../services/api";

const getToday = () =>
  new Date().toISOString().slice(0, 10);

const getCurrentTime = () =>
  new Date().toTimeString().slice(0, 5);

const createEmptyForm = () => ({
  visitId: "",
  shopCode: "",
  workType: "",
  workDescription: "",
  entryDate: getToday(),
  entryTime: getCurrentTime(),
  remark: ""
});

const ShopEntry = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const [formOpen, setFormOpen] = useState(false);

  const [form, setForm] = useState(
    createEmptyForm()
  );

  const [shops, setShops] = useState([]);

  const [yardRows, setYardRows] = useState([]);
  const [shopRows, setShopRows] = useState([]);

  const [selectedCoach, setSelectedCoach] =
    useState(null);

  const [yardSearch, setYardSearch] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [yardPage, setYardPage] =
    useState(1);

  const [page, setPage] =
    useState(1);

  const [yardPagination, setYardPagination] =
    useState({
      page: 1,
      limit: 8,
      total: 0,
      totalPages: 1
    });

  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1
    });

  const [loadingShops, setLoadingShops] =
    useState(false);

  const [yardLoading, setYardLoading] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const notify = (
    message,
    severity = "success"
  ) => {
    setSnack({
      open: true,
      severity,
      message
    });
  };

  /*
   * Existing `shops` master.
   */
  const loadShops = async () => {
    setLoadingShops(true);

    try {
      const response = await api.get(
        "/workshop/shops"
      );

      const data =
        response.data?.data ||
        response.data ||
        [];

      setShops(data);
    } catch (error) {
      notify(
        error.response?.data?.message ||
          "Unable to load shops",
        "error"
      );
    } finally {
      setLoadingShops(false);
    }
  };

  /*
   * Only Yard coaches can be selected.
   */
  const loadYardCoaches = async () => {
    setYardLoading(true);

    try {
      const response = await api.get(
        "/workshop/shop-entry/yard-coaches",
        {
          params: {
            page: yardPage,
            limit: 8,
            search: yardSearch
          }
        }
      );

      const payload =
        response.data || {};

      setYardRows(
        payload.data || []
      );

      setYardPagination(
        payload.pagination || {
          page: yardPage,
          limit: 8,
          total: 0,
          totalPages: 1
        }
      );
    } catch (error) {
      notify(
        error.response?.data?.message ||
          "Unable to load coaches in Yard",
        "error"
      );
    } finally {
      setYardLoading(false);
    }
  };

  /*
   * Existing shop_work records.
   */
  const loadShopEntries = async () => {
    setLoading(true);

    try {
      const response = await api.get(
        "/workshop/shop-entry",
        {
          params: {
            page,
            limit: 10,
            search
          }
        }
      );

      const payload =
        response.data || {};

      setShopRows(
        payload.data || []
      );

      setPagination(
        payload.pagination || {
          page,
          limit: 10,
          total: 0,
          totalPages: 1
        }
      );
    } catch (error) {
      notify(
        error.response?.data?.message ||
          "Unable to load shop entries",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShops();
  }, []);

  useEffect(() => {
    if (formOpen) {
      loadYardCoaches();
    }
  }, [
    formOpen,
    yardPage,
    yardSearch
  ]);

  useEffect(() => {
    loadShopEntries();
  }, [
    page,
    search
  ]);

  const selectedShop = useMemo(
    () =>
      shops.find(
        (item) =>
          item.code === form.shopCode
      ),
    [
      shops,
      form.shopCode
    ]
  );

  const openNewEntry = () => {
    setForm(
      createEmptyForm()
    );

    setSelectedCoach(null);
    setYardSearch("");
    setYardPage(1);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;

    setFormOpen(false);
    setSelectedCoach(null);
    setForm(
      createEmptyForm()
    );
  };

  const chooseCoach = (coach) => {
    setSelectedCoach(coach);

    setForm((previous) => ({
      ...previous,
      visitId: coach.id
    }));
  };

  const changeForm =
    (field) => (event) => {
      setForm((previous) => ({
        ...previous,
        [field]: event.target.value
      }));
    };

  const saveShopEntry = async () => {
    if (!form.visitId) {
      notify(
        "Please select a coach from Yard",
        "warning"
      );
      return;
    }

    if (!form.shopCode) {
      notify(
        "Please select a shop",
        "warning"
      );
      return;
    }

    if (!form.workType.trim()) {
      notify(
        "Please enter work type",
        "warning"
      );
      return;
    }

    if (!form.entryDate) {
      notify(
        "Entry date is required",
        "warning"
      );
      return;
    }

    setSaving(true);

    try {
      const response = await api.post(
        "/workshop/shop-entry",
        {
          visitId: form.visitId,
          shopCode: form.shopCode,
          workType:
            form.workType.trim(),
          workDescription:
            form.workDescription.trim() ||
            null,
          entryDate:
            form.entryDate,
          entryTime:
            form.entryTime || null,
          remark:
            form.remark.trim() ||
            null
        }
      );

      notify(
        response.data?.message ||
          "Shop entry created successfully"
      );

      setFormOpen(false);
      setSelectedCoach(null);
      setForm(
        createEmptyForm()
      );

      await Promise.all([
        loadShopEntries(),
        loadYardCoaches()
      ]);
    } catch (error) {
      notify(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create shop entry",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStatus = (status) => {
    let color = "default";

    if (status === "WAITING") {
      color = "warning";
    }

    if (status === "WORKING") {
      color = "success";
    }

    if (status === "COMPLETED") {
      color = "info";
    }

    if (status === "REWORK") {
      color = "error";
    }

    return (
      <Chip
        size="small"
        label={status}
        color={color}
        variant="outlined"
      />
    );
  };

  const YardCoachCard = ({
    item
  }) => {
    const selected =
      selectedCoach?.id === item.id;

    return (
      <Card
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1,
          borderRadius: 2,
          cursor: "pointer",
          borderColor: selected
            ? "primary.main"
            : "divider",
          bgcolor: selected
            ? "action.selected"
            : "background.paper"
        }}
        onClick={() =>
          chooseCoach(item)
        }
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              fontWeight={700}
              noWrap
            >
              {item.coach_no}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {item.visit_no}
            </Typography>
          </Box>

          <Chip
            label={
              item.yard_code ||
              "YARD"
            }
            size="small"
            variant="outlined"
          />
        </Stack>

        <Typography
          variant="body2"
          sx={{ mt: 0.75 }}
        >
          {item.coach_type_name ||
            item.coach_type_code ||
            "-"}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
        >
          {item.railway_name ||
            item.railway_code ||
            "-"}{" "}
          /{" "}
          {item.base_name ||
            item.base_code ||
            "-"}
        </Typography>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        p: {
          xs: 1,
          sm: 2,
          md: 2.5
        }
      }}
    >
      {/* HEADER */}


      
      <Card
        sx={{
          p: {
            xs: 1.5,
            sm: 2
          },
          borderRadius: 2,
          mb: 2
        }}
      >
        <Stack
  direction={{ xs: "column", sm: "row" }}
  alignItems={{ xs: "stretch", sm: "center" }}
  gap={1.5}
>
  <Box>
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
    >
      <BuildIcon color="primary" />

      <Typography
        variant={isMobile ? "h6" : "h5"}
        fontWeight={700}
      >
        Shop Entry
      </Typography>
    </Stack>

    <Typography
      variant="body2"
      color="text.secondary"
    >
      Move a coach from Yard to a workshop shop and register its work.
    </Typography>
  </Box>

  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={openNewEntry}
    disabled={loadingShops}
    sx={{
      minWidth: 160,
      ml: { xs: 0, sm: "auto" }
    }}
  >
    New Shop Entry
  </Button>
</Stack>
      </Card>

      {/* FORM */}
      {formOpen && (
        <Card
          sx={{
            p: {
              xs: 1.5,
              sm: 2
            },
            borderRadius: 2,
            mb: 2
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography
                fontWeight={700}
              >
                New Shop Entry
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Select a coach currently
                in Yard.
              </Typography>
            </Box>

            <Button
              size="small"
              onClick={
                closeForm
              }
              disabled={saving}
            >
              Close
            </Button>
          </Stack>

          <Divider
            sx={{ my: 2 }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1.1fr 1fr"
              },
              gap: 2
            }}
          >
            {/* YARD COACH LIST */}
            <Card
              variant="outlined"
              sx={{ p: 1.5 }}
            >
              <Typography
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                Coaches in Yard
              </Typography>

              <TextField
                fullWidth
                size="small"
                value={
                  yardSearch
                }
                onChange={(event) => {
                  setYardSearch(
                    event.target.value
                  );
                  setYardPage(1);
                }}
                placeholder="Search coach / visit / railway / base..."
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{
                        mr: 1,
                        color:
                          "text.secondary"
                      }}
                    />
                  )
                }}
              />

              <Box
                sx={{
                  mt: 1.5,
                  minHeight: 250
                }}
              >
                {yardLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent:
                        "center",
                      py: 5
                    }}
                  >
                    <CircularProgress
                      size={28}
                    />
                  </Box>
                ) : yardRows.length ? (
                  yardRows.map(
                    (item) => (
                      <YardCoachCard
                        key={item.id}
                        item={item}
                      />
                    )
                  )
                ) : (
                  <Typography
                    color="text.secondary"
                    sx={{
                      py: 4,
                      textAlign:
                        "center"
                    }}
                  >
                    No coaches currently
                    available in Yard.
                  </Typography>
                )}
              </Box>

              {yardPagination.totalPages >
                1 && (
                <Stack
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Pagination
                    size="small"
                    count={
                      yardPagination.totalPages
                    }
                    page={
                      yardPage
                    }
                    onChange={(
                      _,
                      value
                    ) =>
                      setYardPage(
                        value
                      )
                    }
                  />
                </Stack>
              )}
            </Card>

            {/* DETAILS */}
            <Card
              variant="outlined"
              sx={{ p: 1.5 }}
            >
              <Typography
                fontWeight={700}
                sx={{ mb: 1.5 }}
              >
                Shop Entry Details
              </Typography>

              {selectedCoach ? (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                >
                  <strong>
                    {
                      selectedCoach.coach_no
                    }
                  </strong>{" "}
                  —{" "}
                  {
                    selectedCoach.visit_no
                  }
                  <br />
                  Current Yard:{" "}
                  {
                    selectedCoach.yard_code ||
                    "-"
                  }
                </Alert>
              ) : (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                >
                  Select a coach from the
                  Yard list.
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr"
                  },
                  gap: 1.5
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Shop
                  </InputLabel>

                  <Select
                    value={
                      form.shopCode
                    }
                    label="Shop"
                    onChange={
                      changeForm(
                        "shopCode"
                      )
                    }
                    disabled={
                      loadingShops ||
                      saving
                    }
                  >
                    {shops.map(
                      (shop) => (
                        <MenuItem
                          key={
                            shop.code
                          }
                          value={
                            shop.code
                          }
                        >
                          {shop.name}{" "}
                          ({shop.code})
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Work Type"
                  fullWidth
                  size="small"
                  value={
                    form.workType
                  }
                  onChange={
                    changeForm(
                      "workType"
                    )
                  }
                  disabled={saving}
                  placeholder="e.g. Shell Repair"
                />

                <TextField
                  label="Shop Entry Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={
                    form.entryDate
                  }
                  onChange={
                    changeForm(
                      "entryDate"
                    )
                  }
                  disabled={saving}
                  InputLabelProps={{
                    shrink: true
                  }}
                />

                <TextField
                  label="Shop Entry Time"
                  type="time"
                  size="small"
                  fullWidth
                  value={
                    form.entryTime
                  }
                  onChange={
                    changeForm(
                      "entryTime"
                    )
                  }
                  disabled={saving}
                  InputLabelProps={{
                    shrink: true
                  }}
                />

                <TextField
                  label="Work Description"
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  value={
                    form.workDescription
                  }
                  onChange={
                    changeForm(
                      "workDescription"
                    )
                  }
                  disabled={saving}
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      sm: "1 / -1"
                    }
                  }}
                />

                <TextField
                  label="Remark"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={
                    form.remark
                  }
                  onChange={
                    changeForm(
                      "remark"
                    )
                  }
                  disabled={saving}
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      sm: "1 / -1"
                    }
                  }}
                />
              </Box>

              <Divider
                sx={{ my: 2 }}
              />

              <Stack
                direction={{
                  xs: "column",
                  sm: "row"
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "stretch",
                  sm: "center"
                }}
                gap={1}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {selectedShop
                    ? selectedShop.name
                    : "No shop selected"}
                  {" • "}
                  {form.workType ||
                    "No work type"}
                </Typography>

                <Button
                  variant="contained"
                  onClick={
                    saveShopEntry
                  }
                  disabled={
                    saving ||
                    !selectedCoach ||
                    !form.shopCode ||
                    !form.workType.trim()
                  }
                >
                  {saving ? (
                    <CircularProgress
                      size={22}
                      color="inherit"
                    />
                  ) : (
                    "Save Shop Entry"
                  )}
                </Button>
              </Stack>
            </Card>
          </Box>
        </Card>
      )}

      {/* LIST */}
      <Card
        sx={{
          p: {
            xs: 1.5,
            sm: 2
          },
          borderRadius: 2
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row"
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center"
          }}
          gap={1.5}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography
              fontWeight={700}
            >
              Shop Entries
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Records from shop_work.
            </Typography>
          </Box>

          <TextField
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(
                event.target.value
              );
              setPage(1);
            }}
            placeholder="Search coach, visit, shop, work..."
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    color:
                      "text.secondary"
                  }}
                />
              )
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 330
              }
            }}
          />
        </Stack>

        <Divider />

        {isMobile ? (
          <Box sx={{ pt: 1 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "center",
                  py: 5
                }}
              >
                <CircularProgress
                  size={28}
                />
              </Box>
            ) : shopRows.length ? (
              shopRows.map(
                (item) => (
                  <Card
                    key={item.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Box>
                        <Typography
                          fontWeight={700}
                        >
                          {
                            item.coach_no
                          }
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {
                            item.visit_no
                          }
                        </Typography>
                      </Box>

                      {renderStatus(
                        item.status
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      {item.shop_name ||
                        item.shop_code}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Work Type:{" "}
                      {item.work_type ||
                        "-"}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {
                        item.work_description ||
                        "No work description"
                      }
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Entry:{" "}
                      {item.entry_date ||
                        "-"}{" "}
                      {
                        item.entry_time ||
                        ""
                      }
                    </Typography>
                  </Card>
                )
              )
            ) : (
              <Typography
                color="text.secondary"
                sx={{
                  py: 4,
                  textAlign:
                    "center"
                }}
              >
                No shop entries found.
              </Typography>
            )}
          </Box>
        ) : (
          <TableContainer
            sx={{ mt: 1 }}
          >
            <Table
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Coach No.
                  </TableCell>

                  <TableCell>
                    Visit No.
                  </TableCell>

                  <TableCell>
                    Shop
                  </TableCell>

                  <TableCell>
                    Work Type
                  </TableCell>

                  <TableCell>
                    Description
                  </TableCell>

                  <TableCell>
                    Entry Date
                  </TableCell>

                  <TableCell>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                    >
                      <CircularProgress
                        size={26}
                        sx={{
                          my: 3
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : shopRows.length ? (
                  shopRows.map(
                    (item) => (
                      <TableRow
                        key={item.id}
                        hover
                      >
                        <TableCell
                          sx={{
                            fontWeight:
                              700
                          }}
                        >
                          {
                            item.coach_no
                          }
                        </TableCell>

                        <TableCell>
                          {
                            item.visit_no
                          }
                        </TableCell>

                        <TableCell>
                          {item.shop_name ||
                            item.shop_code}
                        </TableCell>

                        <TableCell>
                          {
                            item.work_type
                          }
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 280
                          }}
                        >
                          {
                            item.work_description ||
                            "-"
                          }
                        </TableCell>

                        <TableCell>
                          {
                            item.entry_date ||
                            "-"
                          }{" "}
                          {
                            item.entry_time ||
                            ""
                          }
                        </TableCell>

                        <TableCell>
                          {renderStatus(
                            item.status
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                    >
                      <Typography
                        color="text.secondary"
                        sx={{
                          py: 4
                        }}
                      >
                        No shop entries
                        found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {pagination.totalPages >
          1 && (
          <Stack
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <Pagination
              count={
                pagination.totalPages
              }
              page={page}
              onChange={(
                _,
                value
              ) =>
                setPage(value)
              }
              color="primary"
            />
          </Stack>
        )}
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() =>
          setSnack(
            (previous) => ({
              ...previous,
              open: false
            })
          )
        }
      >
        <Alert
          severity={
            snack.severity
          }
          onClose={() =>
            setSnack(
              (previous) => ({
                ...previous,
                open: false
              })
            )
          }
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShopEntry;
